import { fullLabel } from "../components/WhereItIs";
import { useBackState } from "@/hooks/useScrollMemory";
import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { prov } from "@/lib/provenance";
import { statusLabel } from "@/lib/status";
import { useAuth } from "@/context/AuthContext";
import { NotesLog } from "@/components/NotesLog";
import { ScanField } from "@/components/ScanField";
import { LotPicker } from "@/components/LotPicker";
import { useOwnTag } from "@/components/search/SearchBox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { scanAccept, scanRefuse } from "@/lib/scanFeedback";
import { SoundToggle } from "@/components/SoundToggle";
import { PackagePlus, Plus, CheckCircle2, Tag, Boxes, ArrowLeft, Loader2, Archive, Search, User, MapPin, Truck, GitMerge, ChevronRight } from "lucide-react";
import { useFloorReturn } from "@/lib/floorReturn";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter,
} from "@/components/ui/alert-dialog";

const DONOR_PAGE = 20;   // truck-picker suggestions shown at once — the TOTAL is stated beside it

export default function FieldCapturePage() {
  const { has } = useAuth();
  const [sp] = useSearchParams();
  const [reloadLotsKey, setReloadLotsKey] = useState(0);   // bumped to make the picker re-read
  const [activeLot, setActiveLot] = useState(null);
  const [binOnly, setBinOnly] = useBackState("binOnly", false);   // show only what is still in the lot's bin
  const [creating, setCreating] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [captured, setCaptured] = useState([]);   // scanned THIS session (newest first)
  const [existing, setExisting] = useState([]);    // already scanned into this lot before this session
  const [loadingLot, setLoadingLot] = useState(false);
  const [count, setCount] = useState(0);
  const [kind, setKind] = useBackState("kind", "standard");    // "standard" | "legacy"
  const [search, setSearch] = useBackState("search", "");
  const [siblings, setSiblings] = useState([]);   // other open lots on THIS lot's truck, from the server
  const [newNotes, setNewNotes] = useState("");
  const [donorHits, setDonorHits] = useState(null);   // {items,total} from the server, not a local filter
  const [donorQuery, setDonorQuery] = useBackState("donorQuery", "");
  const [selectedDonor, setSelectedDonor] = useBackState("selectedDonor", null);
  const [lotLoc, setLotLoc] = useState(null);
  const [movingLot, setMovingLot] = useState(false);
  const [scanningDonor, setScanningDonor] = useState(false);
  const [dupLots, setDupLots] = useState(null);       // open lots already on the picked truck (duplicate warning)
  const [mergeTargetId, setMergeTargetId] = useState("");
  const [mergeConfirm, setMergeConfirm] = useState(false);
  const [merging, setMerging] = useState(false);

  // Unsaved-work signal for the Floor round-trip bar: a typed-but-not-created new lot.
  const fr = useFloorReturn();
  useEffect(() => {
    fr?.setDirty(!activeLot && !!(newLabel.trim() || newNotes.trim() || selectedDonor));
    return () => fr?.setDirty(false);
  }, [activeLot, newLabel, newNotes, selectedDonor, fr]);

  const scanDonor = async (value) => {
    if (!value?.trim()) return;
    setScanningDonor(true);
    try {
      const { data } = await api.post("/donors/resolve-scan", { scan: value.trim() });
      setSelectedDonor(data.donor); setDonorQuery("");
      toast.success(`Truck ${data.donor.stock_number} selected — stamps every part`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setScanningDonor(false); }
  };

  // ENTER (S4, his rule): a truck's own stock # typed exactly picks that truck; anything else stays a list to pick from.
  const ownTag = useOwnTag("donor-search");
  const pickOwnTruck = async (v) => {
    const t = (v || "").trim();
    const hit = await ownTag(t);
    if (hit?.kind === "truck") scanDonor(hit.label || t);
  };

  // The lot list itself is `components/LotPicker` — the SAME picker the floor screen uses, server
  // paged and server searched, so every lot is reachable from either screen. This page only tells
  // it when to re-read (a lot created, moved, merged, or worked and closed).
  const loadLots = () => setReloadLotsKey((k) => k + 1);

  // THE TRUCK PICKER ASKS THE SERVER TOO. It used to pull EVERY non-archived truck on mount
  // (1,224 ms in production) and then show the first 20 matches of a local filter with no
  // indication there were more. Now it is the same indexed truck search the Vehicles page uses,
  // and it says how many it is not showing.
  useEffect(() => {
    const term = donorQuery.trim();
    if (!term) { setDonorHits(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/truck-search", { params: { q: term, page_size: DONOR_PAGE, box: "tag_parts_truck", facets: 0 } });
        setDonorHits({ items: data.items || [], total: data.total ?? (data.items || []).length });
      } catch { setDonorHits({ items: [], total: 0 }); }
    }, 250);
    return () => clearTimeout(t);
  }, [donorQuery]);

  // The merge card's sibling lots, asked of the server for THIS truck.
  useEffect(() => {
    if (!activeLot?.donor_id) { setSiblings([]); return; }
    let live = true;
    api.get("/capture-batches", { params: { paged: true, page_size: 50, donor_id: activeLot.donor_id } })
      .then(({ data }) => {
        if (!live) return;
        setSiblings((data.items || []).filter((l) => l.id !== activeLot.id && l.status !== "closed"));
      })
      .catch(() => { if (live) setSiblings([]); });
    return () => { live = false; };
  }, [activeLot?.donor_id, activeLot?.id]);

  const createLot = async () => {
    const label = newLabel.trim();
    if (!label) { toast.error("Give the group a name (e.g. 'Back row pull — Tues')."); return; }
    if (kind === "standard" && !selectedDonor) { toast.error("Pick the donor truck for this Donor lot — it stamps onto every part."); return; }
    // Duplicate guard: this truck already has an open lot? Warn before creating a second.
    // ASKED OF THE SERVER, NOT OF THE PAGE. It used to filter the locally-loaded lot array, which
    // was fine while that array was every lot — but the list is paged now, so a lot on page 4
    // would have gone unseen and he would have been allowed to open a second lot on the same
    // truck. A guard that only checks what is on screen is not a guard.
    if (kind === "standard" && selectedDonor) {
      try {
        const { data } = await api.get("/capture-batches", {
          params: { paged: true, page_size: 50, donor_id: selectedDonor.id },
        });
        const dups = (data.items || []).filter((l) => l.status !== "closed");
        if (dups.length) { setDupLots(dups); return; }
      } catch { /* a failed check must not block creating a lot — the backend still enforces */ }
    }
    doCreateLot();
  };

  const doCreateLot = async () => {
    const label = newLabel.trim();
    setDupLots(null);
    setCreating(true);
    try {
      const payload = { label, kind, notes: newNotes.trim() };
      if (kind === "standard" && selectedDonor) payload.donor_id = selectedDonor.id;
      const { data } = await api.post("/capture-batches", payload);
      toast.success(kind === "legacy"
        ? `Legacy lot ${data.batch_number} created — parts will be $0 / pre-system`
        : `Donor lot ${data.batch_number} created — bonded to ${data.donor_label || "truck"}`);
      setNewLabel(""); setNewNotes(""); setSelectedDonor(null); setDonorQuery("");
      setActiveLot(data); setLotLoc({ placed: false, label: "Unplaced — scan the lot to a location" }); setCaptured([]); setExisting([]); setCount(0); loadLots();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setCreating(false); }
  };

  const doMerge = async () => {
    if (!mergeTargetId) return;
    setMerging(true);
    try {
      const { data } = await api.post(`/capture-batches/${activeLot.id}/merge-into`, { target_id: mergeTargetId });
      toast.success(`Merged ${data.moved} part${data.moved === 1 ? "" : "s"} into ${data.target}. ${data.source} closed.`);
      setMergeConfirm(false); setMergeTargetId(""); setActiveLot(null); loadLots();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setMerging(false); }
  };

  const openLot = async (lot) => {
    setActiveLot(lot); setLotLoc(lot.current_location || null);
    setCaptured([]); setExisting([]); setCount(lot.total_count || 0);
    setLoadingLot(true);
    try {
      const { data } = await api.get(`/capture-batches/${lot.id}`);
      setExisting(data.parts || []);
      setCount((data.parts || []).length);
      setLotLoc(data.batch?.current_location || lot.current_location || null);
      if (data.batch) setActiveLot((prev) => ({ ...prev, ...data.batch }));
    } catch { /* still usable — scanning works even if the existing list can't load */ }
    finally { setLoadingLot(false); }
  };

  // OPEN A LOT STRAIGHT FROM A LINK — `/capture?lot=<id>`. This is what makes a part's
  // "came from this lot" link work, and it works for a CLOSED lot too, which is his rule:
  // closing is done moving, not gone. One lookup, by id, no list involved.
  useEffect(() => {
    const id = sp.get("lot");
    if (!id || activeLot) return;
    (async () => {
      try {
        const { data } = await api.get(`/capture-batches/${id}`);
        if (data.batch) openLot({ ...data.batch, total_count: (data.parts || []).length });
      } catch { toast.error("That lot could not be opened."); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sp]);

  const moveLot = async (scan, meta) => {
    if (!scan?.trim()) return;
    setMovingLot(true);
    try {
      const { data } = await api.post(`/capture-batches/${activeLot.id}/location`, { location_scan: scan.trim(),
                                                                                     provenance: prov("lot_move", meta) });
      if (data.ok === false) { toast.error(data.message || "Could not move lot"); return; }
      toast.success(`Lot moved to ${fullLabel(data.destination) || data.destination?.short_code || "location"}${data.moved_parts ? ` — ${data.moved_parts} part${data.moved_parts === 1 ? "" : "s"} moved with it` : ""}`);
      const { data: det } = await api.get(`/capture-batches/${activeLot.id}`);
      setLotLoc(det.batch.current_location || null);
      loadLots();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setMovingLot(false); }
  };

  const capture = async (tag_code, meta) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/capture-batches/${activeLot.id}/capture`, { tag_code,
                                                                                    provenance: prov("lot_capture", meta) });
      scanAccept(`${data.part?.short_code || "TAGGED"}`);
      setCaptured((c) => [{ ...data.part, at: Date.now() }, ...c].slice(0, 50));
      setCount(data.batch_total);
      toast.success(`${data.part.short_code} captured`);
    } catch (e) {
      scanRefuse(formatApiErrorDetail(e.response?.data?.detail));
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  // ---------- Lot picker ----------
  if (!activeLot) {
    return (
      <div data-testid="field-capture-page" className="max-w-xl mx-auto">
        <div className="flex items-start justify-between gap-2">
          <h1 className="font-display text-2xl font-bold tracking-tight mb-1 flex items-center gap-2">
            <PackagePlus className="w-6 h-6 text-[#B01E28]" />Tag Parts
          </h1>
          <SoundToggle />
        </div>
        <p className="text-sm text-slate-500 mb-5">Scan a tag, drop it in a lot — done. Identify the details later in Identify &amp; Completion. Use a <span className="font-semibold">Legacy lot</span> for pre-system parts ($0, auto-flagged).</p>

        <div className="border border-slate-200 bg-white rounded-xl p-4 mb-5">
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Start a new capture group</Label>
          <div className="flex gap-2 mt-2 mb-3" data-testid="capture-kind-toggle">
            <button data-testid="capture-kind-standard" type="button" onClick={() => setKind("standard")}
              className={`flex-1 rounded-lg border px-3 py-3 text-sm font-semibold transition-colors ${kind === "standard" ? "border-[#E0781A] bg-[#FFF9F3] text-[#C76714]" : "border-slate-200 text-slate-500 hover:border-slate-300"}`}>
              <Boxes className="w-4 h-4 inline mr-1.5 -mt-0.5" />Donor lot
            </button>
            <button data-testid="capture-kind-legacy" type="button" onClick={() => setKind("legacy")}
              className={`flex-1 rounded-lg border px-3 py-3 text-sm font-semibold transition-colors ${kind === "legacy" ? "border-[#7A4FB5] bg-[#F6F1FB] text-[#5E3A93]" : "border-slate-200 text-slate-500 hover:border-slate-300"}`}>
              <Archive className="w-4 h-4 inline mr-1.5 -mt-0.5" />Legacy lot
            </button>
          </div>
          {kind === "legacy" && (
            <p data-testid="legacy-hint" className="text-xs text-[#5E3A93] bg-[#F6F1FB] border border-[#7A4FB5]/30 rounded-lg px-3 py-2 mb-2">
              Pre-system / pre-existing parts. Everything captured here is auto-flagged <span className="font-mono font-bold">legacy</span> with <span className="font-mono font-bold">$0</span> cost — no donor recovery impact.
            </p>
          )}
          {kind === "standard" && (
            <div className="mb-2" data-testid="donor-picker">
              <Label className="text-[11px] font-bold uppercase tracking-wider text-[#C76714] flex items-center gap-1.5 mb-1"><User className="w-3.5 h-3.5" />Donor truck — stamped onto every part</Label>
              {selectedDonor ? (
                <div data-testid="donor-selected" className="flex items-center justify-between gap-2 border border-[#E0781A] bg-[#FFF9F3] rounded-lg px-3 py-2">
                  <div className="min-w-0">
                    <span className="font-mono font-semibold text-slate-800">{selectedDonor.stock_number}</span>
                    <span className="text-xs text-slate-500 ml-2 truncate">{[selectedDonor.year, selectedDonor.make, selectedDonor.model].filter(Boolean).join(" ")}{selectedDonor.vin ? ` · VIN ${selectedDonor.vin}` : ""}</span>
                  </div>
                  <button data-testid="donor-clear" type="button" onClick={() => setSelectedDonor(null)} className="text-xs text-slate-400 hover:text-[#B01E28] underline shrink-0">Change</button>
                </div>
              ) : (
                <>
                  <div data-testid="donor-scan-wrap" className="mb-2">
                    <ScanField onScan={scanDonor} busy={scanningDonor} testid="donor-scan"
                      label={null} placeholder="Scan the truck's QR / label…" submitLabel="Scan truck" />
                    <p className="text-[10px] text-slate-400 mt-1">Scan is the fast path in the yard. No label / at a PC? Search below.</p>
                  </div>
                  <div className="relative">
                    <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <Input data-testid="donor-search" value={donorQuery} onChange={(e) => setDonorQuery(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); pickOwnTruck(e.currentTarget.value); } }}
                      enterKeyHint="search"
                      placeholder="…or search — stock#, VIN, make/model" className="pl-9 h-10 rounded-lg border-slate-200" />
                  {donorQuery.trim() && (
                    <div data-testid="donor-options" className="absolute z-10 mt-1 w-full max-h-56 overflow-auto bg-white border border-slate-200 rounded-lg shadow-lg">
                      {donorHits === null && <div className="px-3 py-2 text-xs text-slate-400">Searching…</div>}
                      {(donorHits?.items || []).map((d) => (
                        <button key={d.id} data-testid={`donor-option-${d.stock_number}`} type="button"
                          onClick={() => { setSelectedDonor(d); setDonorQuery(""); }}
                          className="w-full text-left px-3 py-2 hover:bg-[#FFF9F3] border-b border-slate-50 last:border-0">
                          <span className="font-mono font-semibold text-slate-800 text-sm">{d.stock_number}</span>
                          <span className="text-xs text-slate-500 ml-2">{[d.year, d.make, d.model].filter(Boolean).join(" ")}</span>
                          {d.vin && <span className="block font-mono text-[10px] text-slate-500 mt-0.5">VIN {d.vin}</span>}
                        </button>
                      ))}
                      {/* IT SAYS WHAT IT IS NOT SHOWING. This list used to cut silently at 20 of
                          however many matched, so a truck could be excluded from the picker with
                          no way to know it existed. */}
                      {donorHits && donorHits.total > donorHits.items.length && (
                        <div data-testid="donor-options-more" className="px-3 py-2 text-[11px] font-semibold text-[#C76714] bg-[#FFF9F3] border-t border-slate-100">
                          Showing {donorHits.items.length} of {donorHits.total} matching trucks — type more to narrow it down.
                        </div>
                      )}
                      {donorHits && donorHits.items.length === 0 && (
                        <div className="px-3 py-2 text-xs text-slate-400">No matching truck.</div>
                      )}
                    </div>
                  )}
                  </div>
                </>
              )}
            </div>
          )}
          <div className="flex gap-2">
            <Input data-testid="new-lot-label" value={newLabel} onChange={(e) => setNewLabel(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && createLot()}
              placeholder={kind === "legacy" ? "e.g. Old shelf stock — Bay 3" : "e.g. Back row pull — Tues"} className="h-11" />
            <Button data-testid="create-lot-btn" onClick={createLot} disabled={creating}
              className={`rounded-lg text-white h-11 px-4 shrink-0 ${kind === "legacy" ? "bg-[#7A4FB5] hover:bg-[#5E3A93]" : "bg-[#E0781A] hover:bg-[#C76714]"}`}>
              {creating ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Plus className="w-5 h-5 mr-1" />{kind === "legacy" ? "New legacy lot" : "New donor lot"}</>}
            </Button>
          </div>
          <textarea data-testid="new-lot-notes" value={newNotes} onChange={(e) => setNewNotes(e.target.value)}
            rows={2} placeholder="Optional notes for this lot (e.g. which row, condition, who's working it)…"
            className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm resize-y focus:outline-none focus:border-[#E0781A]" />
        </div>

        <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Open lots</Label>
        <div className="mt-2">
          <LotPicker variant="desk" onPick={openLot} reloadKey={reloadLotsKey} />
        </div>

        <AlertDialog open={!!dupLots} onOpenChange={(o) => !o && setDupLots(null)}>
          <AlertDialogContent data-testid="dup-lot-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>This truck already has an open lot</AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="text-sm text-slate-600 space-y-2">
                  <div className="flex items-center gap-1.5 text-[#C76714]"><Truck className="w-4 h-4" /><span className="font-mono font-semibold">{selectedDonor?.stock_number}</span>{selectedDonor && <span className="text-slate-500">· {[selectedDonor.year, selectedDonor.make, selectedDonor.model].filter(Boolean).join(" ")}</span>}</div>
                  <p>Open lot{(dupLots?.length || 0) === 1 ? "" : "s"} already bonded to this truck:</p>
                  <div className="space-y-1">
                    {(dupLots || []).map((l) => (
                      <div key={l.id} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 flex items-center justify-between">
                        <span><span className="font-mono font-semibold text-slate-800">{l.batch_number}</span> <span className="text-slate-500">— {l.label}</span></span>
                        <span className="font-mono text-slate-500">{l.total_count} parts</span>
                      </div>
                    ))}
                  </div>
                  <p>Add to the existing lot instead of creating a duplicate?</p>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="flex-col sm:flex-row gap-2">
              <Button variant="ghost" onClick={() => setDupLots(null)} data-testid="dup-cancel">Cancel</Button>
              <Button variant="outline" onClick={() => { const l = dupLots[0]; setDupLots(null); openLot(l); }} data-testid="dup-open-existing">Open {dupLots?.[0]?.batch_number}</Button>
              <Button className="bg-[#E0781A] hover:bg-[#C76714] text-white" onClick={doCreateLot} data-testid="dup-create-anyway">Create new lot anyway</Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    );
  }

  // ---------- Capture view ----------
  // WHAT IS STILL IN THE BIN — the "N in bin" chip filters this list to exactly those parts, and
  // every row walks through to the part itself (his instruction, 2026-10-14).
  const shownParts = binOnly
    ? existing.filter((p) => String(p.location?.id || "") === String(activeLot?.container_id || ""))
    : existing;

  return (
    <div data-testid="field-capture-active" className="max-w-xl mx-auto">
      <button data-testid="back-to-lots" onClick={() => { setActiveLot(null); loadLots(); }}
        className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-3">
        <ArrowLeft className="w-4 h-4" />All lots
      </button>

      <div className="flex items-center justify-between mb-4">
        <div className="min-w-0">
          <div className="font-mono font-bold text-slate-800 flex items-center gap-2">{activeLot.batch_number}
            {activeLot.kind === "legacy" && <Badge data-testid="active-legacy-badge" className="rounded-full border-0 bg-[#F6F1FB] text-[#5E3A93] text-[9px] font-mono px-1.5">LEGACY · $0</Badge>}
          </div>
          <div className="text-sm text-slate-500 truncate">{activeLot.label}</div>
          {activeLot.donor && (
            <div data-testid="active-lot-truck" className="text-[11px] text-[#C76714] flex items-center gap-1 mt-0.5 flex-wrap">
              <Truck className="w-3 h-3 shrink-0" />
              <span className="font-mono font-semibold">{activeLot.donor.stock_number}</span>
              {activeLot.donor.ymm && <span className="text-slate-500">· {activeLot.donor.ymm}</span>}
              {activeLot.donor.vin && <span className="font-mono text-slate-400">· VIN {activeLot.donor.vin}</span>}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <SoundToggle />
          <Badge data-testid="lot-count" className="rounded-full bg-[#1F1F1F] text-white font-mono text-sm px-3 py-1">{count} captured</Badge>
          {/* STILL IN THE BIN — his rule, 2026-10-14: the lot is finished when its bin is empty,
              so the number that decides it is on screen next to the count. */}
          {(activeLot.in_crate ?? 0) > 0 ? (
            <button type="button" data-testid="lot-in-bin" onClick={() => setBinOnly((b) => !b)}
              className={`rounded-full font-mono text-sm px-3 py-1 border ${binOnly ? "bg-[#C76714] text-white border-[#C76714]" : "bg-[#FFF1E8] text-[#C76714] border-[#E0781A]/40 hover:bg-[#FFE6D2]"}`}>
              {activeLot.in_crate} in bin
            </button>
          ) : count > 0 ? (
            <Badge data-testid="lot-bin-empty" className="rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 font-mono text-sm px-3 py-1">bin empty</Badge>
          ) : null}
        </div>
      </div>

      {/* A CLOSED LOT IS VIEWABLE, NOT GONE — his instruction 2026-10-07. Everything below is
          readable: the notes, the history, the parts. Only tagging into it is off. */}
      {activeLot.status === "closed" ? (
        <div data-testid="lot-closed-banner" className="border-2 border-slate-800 bg-slate-50 rounded-xl p-4 mb-4">
          <div className="font-bold text-slate-800">This lot is closed — done moving, not gone.</div>
          <div className="text-sm text-slate-600 mt-1">
            Closed {activeLot.closed_at ? new Date(activeLot.closed_at).toLocaleDateString() : ""}
            {activeLot.closed_by ? ` by ${activeLot.closed_by}` : ""}
            {activeLot.closed_reason ? ` — ${activeLot.closed_reason}` : ""}.
            Its notes, history and parts all stay here. To tag into it again, re-open it.
          </div>
          <Button data-testid="lot-reopen" variant="outline" disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await api.post(`/capture-batches/${activeLot.id}/status`, { status: "open" });
                // `GET /capture-batches/{id}` answers {batch, parts, total, shown} — the LOT is
                // under .batch. Assigning the whole envelope wiped the lot's id and number off
                // the open screen (the next scan would have posted to .../undefined/capture) and
                // the toast read "undefined re-opened". Merge the batch, same as openLot does.
                const { data } = await api.get(`/capture-batches/${activeLot.id}`);
                if (data.batch) setActiveLot((prev) => ({ ...prev, ...data.batch }));
                setExisting(data.parts || []);
                setCount((data.parts || []).length);
                toast.success(`${data.batch?.batch_number || activeLot.batch_number} re-opened`);
              } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
            }}
            className="mt-3 h-10 rounded-lg border-slate-300 font-semibold">Re-open this lot</Button>
        </div>
      ) : (
        <div className="border-2 border-[#E0781A]/30 bg-[#FFF9F3] rounded-xl p-4 mb-4">
          <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-[#C76714] mb-2">
            <Tag className="w-3.5 h-3.5" />Scan a pre-printed FAS tag — that's all you need
          </div>
          <ScanField onScan={capture} busy={busy} testid="capture-scan" continuous
            label={null} placeholder="Scan / type FAS tag…" submitLabel="Capture" />
        </div>
      )}


      <div className="border border-slate-200 bg-white rounded-xl p-4 mb-4">
        <div className="flex items-center gap-1.5 mb-2">
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><Tag className="w-3.5 h-3.5" />Lot notes</Label>
          <span className="text-[11px] text-slate-400">— permanent, timestamped, attributed. These follow the lot to Identify.</span>
        </div>
        <NotesLog recordType="lot" recordId={activeLot.id} canAdd={has("edit_lot_notes")} />
      </div>

      <div className="border border-slate-200 bg-white rounded-xl p-4 mb-4" data-testid="lot-location-card">
        <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5 mb-2"><MapPin className="w-3.5 h-3.5" />Lot location</Label>
        <div data-testid="lot-current-location" className={`text-sm mb-2 ${lotLoc?.placed ? "text-slate-700" : "text-slate-400"}`}>
          {lotLoc?.placed
            ? <span>This crate is at <span className="font-mono font-semibold text-slate-800">{lotLoc.label}</span>. Moving the lot moves all its in-lot parts together.</span>
            : <span>{lotLoc?.label || "Unplaced — scan the lot to a location."}</span>}
        </div>
        <ScanField onScan={moveLot} busy={movingLot} testid="lot-move-scan" autoFocus={false}
          label={null} placeholder="Scan destination bin/location to move this lot…" submitLabel="Move lot" />
      </div>

      {count === 0 && activeLot.status !== "closed" && (
        // CLOSING AN EMPTY LOT FROM THE DESK — his instruction 2026-10-08, for the lots created by
        // accident (three of his had a scanned QR URL pasted into the name field). Only ever shown
        // for an EMPTY lot: finishing a lot with parts in it stays a floor job, because that one
        // needs the crate scanned to where it went. The SERVER holds the rule — it refuses a lot
        // under a week old or one that ever held a part — so this cannot get around it.
        <div data-testid="lot-empty-close-card" className="border border-slate-300 bg-slate-50 rounded-xl p-4 mb-4">
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5 mb-2"><Archive className="w-3.5 h-3.5" />This lot is empty</Label>
          <p className="text-sm text-slate-600 mb-3">Nothing has ever been tagged into <span className="font-mono font-semibold">{activeLot.batch_number}</span>. If it was made by mistake, close it as never used — it stays searchable with its full history, and nothing is deleted.</p>
          <Button data-testid="lot-close-empty" variant="outline" disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await api.post(`/capture-batches/${activeLot.id}/finish`,
                               { close_without_moving: true, reason: "empty — never used" });
                const { data } = await api.get(`/capture-batches/${activeLot.id}`);
                if (data.batch) setActiveLot((prev) => ({ ...prev, ...data.batch }));
                setReloadLotsKey((k) => k + 1);
                toast.success(`${activeLot.batch_number} closed — empty, never used`);
              } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
              finally { setBusy(false); }
            }}
            className="h-10 rounded-lg border-slate-300 font-semibold">Close it — empty, never used</Button>
          <p className="text-[11px] text-slate-400 mt-2">A lot can only be closed this way once it is <span className="font-semibold">more than a week old</span> — every lot starts empty, so a new one may simply be about to be used.</p>
        </div>
      )}

      {(() => {
        // EVERY open lot on this truck, fetched from the server when the lot was opened — not the
        // ones that happened to be on the loaded page. With the list paged, reading the local
        // array would hide a duplicate sitting on page four and quietly remove the fix for it.
        if (!siblings.length) return null;
        const tgt = siblings.find((l) => l.id === mergeTargetId) || null;
        return (
          <div data-testid="lot-merge-card" className="border-2 border-[#B01E28]/30 bg-[#FDF3F4] rounded-xl p-4 mb-4">
            <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-[#B01E28] mb-1">
              <GitMerge className="w-3.5 h-3.5" />This truck has {siblings.length} other open lot{siblings.length === 1 ? "" : "s"}
            </div>
            <p className="text-xs text-slate-600 mb-2">Merge this lot into one of them to fix a duplicate. This lot (<span className="font-mono font-semibold">{activeLot.batch_number}</span>) will empty and close — kept as history.</p>
            <div className="flex gap-2">
              <select data-testid="merge-target-select" value={mergeTargetId} onChange={(e) => setMergeTargetId(e.target.value)}
                className="flex-1 h-10 rounded-lg border border-slate-200 px-2 text-sm bg-white focus:outline-none focus:border-[#B01E28]">
                <option value="">Merge into…</option>
                {siblings.map((l) => (
                  <option key={l.id} value={l.id}>{l.batch_number} — {l.label} ({l.total_count} parts)</option>
                ))}
              </select>
              <Button data-testid="merge-open-confirm" onClick={() => setMergeConfirm(true)} disabled={!mergeTargetId}
                className="rounded-lg h-10 px-4 shrink-0 bg-[#B01E28] hover:bg-[#8f1820] text-white">
                <GitMerge className="w-4 h-4 mr-1" />Merge
              </Button>
            </div>

            <AlertDialog open={mergeConfirm} onOpenChange={(o) => !merging && setMergeConfirm(o)}>
              <AlertDialogContent data-testid="merge-confirm-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle>Merge lots for this truck?</AlertDialogTitle>
                  <AlertDialogDescription asChild>
                    <div className="text-sm text-slate-600 space-y-2">
                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-1">
                        <div className="flex items-center gap-1.5 text-[#C76714]"><Truck className="w-3.5 h-3.5" /><span className="font-mono font-semibold">{activeLot.donor?.stock_number}</span>{activeLot.donor?.ymm && <span className="text-slate-500">· {activeLot.donor.ymm}</span>}</div>
                        <div><span className="font-mono font-semibold text-[#B01E28]">{activeLot.batch_number}</span> <span className="text-slate-500">({activeLot.total_count} part{activeLot.total_count === 1 ? "" : "s"})</span> → <span className="font-mono font-semibold text-emerald-700">{tgt?.batch_number}</span> <span className="text-slate-500">({tgt?.total_count} part{tgt?.total_count === 1 ? "" : "s"})</span></div>
                      </div>
                      <p>All <span className="font-semibold">{activeLot.total_count}</span> part{activeLot.total_count === 1 ? "" : "s"} move into <span className="font-mono font-semibold">{tgt?.batch_number}</span>, then <span className="font-mono font-semibold">{activeLot.batch_number}</span> closes. Donor bond &amp; values are unchanged. Can't be undone from here.</p>
                    </div>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <Button variant="outline" disabled={merging} onClick={() => setMergeConfirm(false)} data-testid="merge-cancel">Cancel</Button>
                  <Button className="bg-[#B01E28] hover:bg-[#8f1820] text-white" disabled={merging} onClick={doMerge} data-testid="merge-confirm-yes">
                    {merging ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <GitMerge className="w-4 h-4 mr-1" />}Merge &amp; close {activeLot.batch_number}
                  </Button>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        );
      })()}

      <Label className="text-xs font-bold uppercase tracking-wider text-emerald-700 flex items-center gap-1.5">
        <CheckCircle2 className="w-3.5 h-3.5" />Added this session ({captured.length})
      </Label>
      <div className="mt-2 space-y-1.5" data-testid="captured-session-list">
        {captured.length === 0 ? (
          <div className="border border-dashed border-slate-200 rounded-xl p-6 text-center text-sm text-slate-400">Scan a tag above. Each captured part lands in Identify &amp; Completion.</div>
        ) : captured.map((p) => (
          <div key={p.id} data-testid={`captured-${p.short_code}`}
            className="flex items-center gap-2.5 border border-emerald-200 bg-emerald-50/60 rounded-lg px-3 py-2.5">
            <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />
            <span className="font-mono font-semibold text-slate-800">{p.short_code}</span>
            <Badge className="rounded-full border-0 bg-emerald-600 text-white text-[9px] font-mono px-1.5">NEW</Badge>
            <span className="ml-auto text-[10px] uppercase tracking-wider text-amber-600 font-mono">needs ID</span>
          </div>
        ))}
      </div>

      <Label className="mt-5 block text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
        <Boxes className="w-3.5 h-3.5" />
        {binOnly ? `Still in the bin (${shownParts.length})` : `Already in this lot (${existing.length})`}
        {binOnly && (
          <button type="button" data-testid="lot-bin-only-off" onClick={() => setBinOnly(false)}
            className="ml-1 normal-case tracking-normal font-semibold text-[#B01E28] hover:underline">show all</button>
        )}
      </Label>
      <div className="mt-2 space-y-1.5" data-testid="existing-lot-list">
        {loadingLot ? (
          <div className="border border-dashed border-slate-200 rounded-xl p-6 text-center text-sm text-slate-400 flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Loading what's already in this lot…</div>
        ) : shownParts.length === 0 ? (
          <div className="border border-dashed border-slate-200 rounded-xl p-6 text-center text-sm text-slate-400">
            {binOnly ? "Nothing left in the bin — this lot finishes itself with its last part." : "Nothing scanned into this lot yet."}
          </div>
        ) : shownParts.map((p) => (
          <Link key={p.id} to={`/parts/${p.id}`} data-testid={`existing-${p.short_code}`}
            className="flex items-center gap-2.5 border border-slate-200 bg-white rounded-lg px-3 py-2.5 hover:border-[#E0781A] hover:bg-[#FFF9F3]">
            <Tag className="w-4 h-4 text-slate-400 shrink-0" />
            <span className="font-mono font-semibold text-slate-800">{p.short_code}</span>
            {p.name && <span className="text-sm text-slate-500 truncate">{p.name}</span>}
            {String(p.location?.id || "") === String(activeLot.container_id || "") && (
              <span className="text-[10px] uppercase tracking-wider text-[#C76714] font-mono">in bin</span>
            )}
            <span className="ml-auto text-[10px] uppercase tracking-wider text-slate-400 font-mono">{statusLabel(p.status)}</span>
            <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
          </Link>
        ))}
      </div>

      <Button data-testid="done-lot-btn" onClick={() => { setActiveLot(null); loadLots(); }}
        className="mt-5 w-full h-12 rounded-lg bg-[#1F1F1F] hover:bg-black text-white text-base">
        <CheckCircle2 className="w-5 h-5 mr-2" />Done{captured.length ? ` — ${captured.length} added this session` : ""}
      </Button>
    </div>
  );
}
